// A producer's Seedbed session, for the web app (the account build).
//
// The admin app holds one shared secret; this holds a real account's tokens —
// the same pair the extension gets from this deployment's seedbed-api (one
// channel per deployment; see worker/account.ts): a one-hour access token and
// a rotating refresh token.
// The refresh is single-flight within a tab, and serialised across tabs with a
// Web Lock: every tab reads the same stored session, so when the access token
// comes due they would all present the same refresh token at once, and the
// API's rotation leaves the loser signed out — which clears the shared session
// and signs every tab out with it (#146). Inside the lock a tab re-reads
// storage: if a sibling has already rotated the pair, that is the answer.

import type { AuthResponse, AuthUser } from "@shared/api-types.js";

const SESSION_KEY = "seedbed.account";

// Who is signed in changes under the whole page (sign in, sign out, a refresh
// that fails): components read it through `useSession`, which subscribes here.
const listeners = new Set<() => void>();

export function subscribeSession(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function changed(): void {
  cachedRaw = undefined;
  for (const l of listeners) l();
}

// Another tab signing in, out, or refreshing writes the same key: tell this
// tab's subscribers, so the header and the pages follow without a reload.
if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
  window.addEventListener("storage", (ev: StorageEvent) => {
    if (ev.key === SESSION_KEY || ev.key === null) changed();
  });
}

// getSession parses localStorage; useSyncExternalStore needs the SAME object
// back while nothing changed, so the parse is cached against the raw string.
let cachedRaw: string | null | undefined;
let cachedSession: AccountSession | null = null;

export interface AccountSession {
  accessToken: string;
  accessExpiresAt: string;
  refreshToken: string;
  user: AuthUser;
}

export function getSession(): AccountSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (raw === cachedRaw) return cachedSession;
    cachedRaw = raw;
    cachedSession = null;
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<AccountSession>;
    if (!parsed.accessToken || !parsed.refreshToken || !parsed.user) return null;
    cachedSession = parsed as AccountSession;
    return cachedSession;
  } catch {
    return null;
  }
}

function saveSession(res: AuthResponse): AccountSession {
  const session: AccountSession = {
    accessToken: res.accessToken,
    accessExpiresAt: res.accessExpiresAt,
    refreshToken: res.refreshToken,
    user: res.user,
  };
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  changed();
  return getSession() ?? session;
}

export function clearSession(): void {
  localStorage.removeItem(SESSION_KEY);
  changed();
}

export function accessToken(): string {
  return getSession()?.accessToken ?? "";
}

async function post(path: string, body: unknown, signal?: AbortSignal): Promise<AuthResponse> {
  const res = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal,
  });
  const json = (await res.json().catch(() => null)) as (AuthResponse & { error?: string }) | null;
  if (!res.ok || !json?.accessToken) {
    throw new Error(json?.error ?? `Request failed (${res.status})`);
  }
  return json;
}

export async function signIn(email: string, password: string): Promise<AccountSession> {
  return saveSession(await post("/api/account/signin", { email, password }));
}

/** A new account — the same one the extension signs in with. */
export async function signUp(email: string, password: string, username: string): Promise<AccountSession> {
  return saveSession(await post("/api/auth/signup", { email, password, username, surface: "web" }));
}

/** Forgot password: the API emails a reset link if the address has an
 * account, and answers the same either way. */
export async function requestPasswordReset(email: string): Promise<void> {
  const res = await fetch("/api/auth/forgot", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const json = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(json?.error ?? `Request failed (${res.status})`);
  }
}

/** A new password from a reset link's token; signs in. */
export async function resetPassword(token: string, password: string): Promise<AccountSession> {
  return saveSession(await post("/api/auth/reset", { token, password }));
}

let inflight: Promise<AccountSession | null> | null = null;

/**
 * How long a refresh may take. The app renders nothing until a stale session
 * is refreshed (AccountApp), so a request that never answers — a phone on a
 * bad connection — was a blank page for as long as it hung.
 */
const REFRESH_TIMEOUT_MS = 8_000;

/** The Web Lock every tab of this origin takes to refresh the shared session. */
export const REFRESH_LOCK = "seedbed.account.refresh";

/**
 * Trade the refresh token for a new pair. Null means the session is over —
 * rotated elsewhere, expired, or signed out — and the caller should show the
 * sign-in form rather than retry. Also null, with the session kept, when the
 * API did not answer in time.
 */
export function refreshSession(): Promise<AccountSession | null> {
  if (inflight) return inflight;
  const seen = getSession();
  if (!seen) return Promise.resolve(null);
  inflight = acrossTabs(() => rotate(seen.refreshToken)).finally(() => {
    inflight = null;
  });
  return inflight;
}

/**
 * One tab at a time, when the browser can promise that. Without Web Locks
 * (an old WebView, a test) the tabs race as before and the API's reuse grace
 * is what keeps the loser signed in.
 */
function acrossTabs<T>(work: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  if (!locks) return work();
  return locks.request(REFRESH_LOCK, work) as Promise<T>;
}

/** Inside the lock: refresh the pair this tab set out with, unless a sibling already has. */
async function rotate(presented: string): Promise<AccountSession | null> {
  const current = getSession();
  if (!current) return null;
  // A sibling tab rotated the pair while this one waited for the lock. Its
  // new pair is in storage; presenting the old one would only spend the grace.
  if (current.refreshToken !== presented) return current;
  try {
    return saveSession(await post("/api/account/refresh", { refreshToken: current.refreshToken }, AbortSignal.timeout(REFRESH_TIMEOUT_MS)));
  } catch (err: unknown) {
    // A timeout says nothing about the session: keep it, and let the next
    // refresh (AccountApp's timer, or the next 401) try again. Anything
    // else is the API saying no.
    if (!(err instanceof DOMException && err.name === "TimeoutError")) clearSession();
    return null;
  }
}

/** Whether the access token is past, or within a minute of, its expiry. */
export function needsRefresh(session: AccountSession, now = Date.now()): boolean {
  const exp = Date.parse(session.accessExpiresAt);
  return !Number.isFinite(exp) || exp - now < 60_000;
}
